/**
 * Launch contract for utm_content-driven post-login landing.
 *
 * Frontend pages live on `feat/post-login-cta-home-variants` (CtaHome on HomePage).
 * Routing uses campaignHomeIntent + existing paths — not separate /post-login/* URLs.
 */

export const POST_LOGIN_CAMPAIGN_INTENT_SOURCE = 'post-login-utm';

export const POST_LOGIN_UTM_CONTENT = Object.freeze({
  SUGGESTED_PAYMENT_PLAN: 'suggested_payment_plan',
  SETTLEMENT_OFFER: 'settlement_offer',
  CUSTOM_PAYMENT_PLAN: 'custom_payment_plan',
});

export const POST_LOGIN_UTM_CONTENT_VALUES = Object.freeze(
  Object.values(POST_LOGIN_UTM_CONTENT)
);

export const POST_LOGIN_EXPERIENCE_KEY = Object.freeze({
  SUGGESTED_PAYMENT_PLAN: 'SUGGESTED_PAYMENT_PLAN',
  SETTLEMENT_OFFER: 'SETTLEMENT_OFFER',
  CUSTOM_PAYMENT_PLAN: 'CUSTOM_PAYMENT_PLAN',
  HOME: 'HOME',
});

export const POST_LOGIN_FALLBACK_REASON = Object.freeze({
  UNKNOWN_UTM: 'UNKNOWN_UTM',
  OFFER_UNAVAILABLE: 'OFFER_UNAVAILABLE',
  OFFER_EXPIRED: 'OFFER_EXPIRED',
});

/** @type {Readonly<Record<string, string>>} */
export const UTM_CONTENT_TO_EXPERIENCE_KEY = Object.freeze({
  [POST_LOGIN_UTM_CONTENT.SUGGESTED_PAYMENT_PLAN]:
    POST_LOGIN_EXPERIENCE_KEY.SUGGESTED_PAYMENT_PLAN,
  [POST_LOGIN_UTM_CONTENT.SETTLEMENT_OFFER]:
    POST_LOGIN_EXPERIENCE_KEY.SETTLEMENT_OFFER,
  [POST_LOGIN_UTM_CONTENT.CUSTOM_PAYMENT_PLAN]:
    POST_LOGIN_EXPERIENCE_KEY.CUSTOM_PAYMENT_PLAN,
});

/**
 * campaignHomeIntent.cta values from feat/post-login-cta-home-variants (CAMPAIGN_HOME_CTA_VARIANTS).
 * @type {Readonly<Record<string, string>>}
 */
export const POST_LOGIN_CAMPAIGN_HOME_CTA = Object.freeze({
  [POST_LOGIN_EXPERIENCE_KEY.SUGGESTED_PAYMENT_PLAN]: 'suggested-plan',
  [POST_LOGIN_EXPERIENCE_KEY.SETTLEMENT_OFFER]: 'settlement-offer-a',
  [POST_LOGIN_EXPERIENCE_KEY.CUSTOM_PAYMENT_PLAN]: 'custom-plan',
});

/**
 * Post-login redirect targets — sync with apps/payment_portal/src/routes/paths.js.
 * @type {Readonly<Record<string, string>>}
 */
export const POST_LOGIN_EXPERIENCE_ROUTES = Object.freeze({
  [POST_LOGIN_EXPERIENCE_KEY.SUGGESTED_PAYMENT_PLAN]: '/',
  [POST_LOGIN_EXPERIENCE_KEY.SETTLEMENT_OFFER]: '/',
  [POST_LOGIN_EXPERIENCE_KEY.CUSTOM_PAYMENT_PLAN]: '/payment-plan',
  [POST_LOGIN_EXPERIENCE_KEY.HOME]: '/',
});

export const isValidPostLoginUtmContent = (value) =>
  POST_LOGIN_UTM_CONTENT_VALUES.includes(String(value || '').trim());

export const getExperienceKeyForUtmContent = (utmContent) => {
  const normalized = String(utmContent || '').trim();
  return UTM_CONTENT_TO_EXPERIENCE_KEY[normalized] || POST_LOGIN_EXPERIENCE_KEY.HOME;
};

export const getRouteForExperienceKey = (experienceKey) =>
  POST_LOGIN_EXPERIENCE_ROUTES[String(experienceKey || '').trim()] ||
  POST_LOGIN_EXPERIENCE_ROUTES[POST_LOGIN_EXPERIENCE_KEY.HOME];

export const getCampaignHomeCtaForExperienceKey = (experienceKey) =>
  POST_LOGIN_CAMPAIGN_HOME_CTA[String(experienceKey || '').trim()] || null;

/**
 * Resolved post-login navigation target for BE3.
 * @returns {{ route: string, campaignHomeCta: string|null }}
 */
export const getPostLoginDestination = (experienceKey) => ({
  route: getRouteForExperienceKey(experienceKey),
  campaignHomeCta: getCampaignHomeCtaForExperienceKey(experienceKey),
});
